// src/components/CalendarView/CalendarDayView.tsx
import React, { useMemo } from 'react';
import type { CalendarEvent, ActionMenuConfig } from './types';
import { CalendarEventCard } from './CalendarEventCard';
import { getCalendarEventKey } from './calendarUtils';

function toDateKey(d: Date): string
{
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function getEventDateKey(event: CalendarEvent): string
{
  const d = new Date(event.Date);
  if (isNaN(d.getTime())) return '';
  return toDateKey(d);
}

interface CalendarDayViewProps {
  events: CalendarEvent[];
  currentDate: Date;
  isLoading: boolean;
  onOpenInfoWindow: (conversationId: number) => void;
  actionMenuConfig: ActionMenuConfig;
  onDuplicateItem?: (e: any, conv: any) => void;
}

export const CalendarDayView: React.FC<CalendarDayViewProps> = ({
  events,
  currentDate,
  isLoading,
  onOpenInfoWindow,
  actionMenuConfig,
  onDuplicateItem,
}) =>
{
  const key = toDateKey(currentDate);

  const dayEvents = useMemo(() =>
  {
    return events.filter(ev => getEventDateKey(ev) === key);
  }, [events, key]);

  const dow = currentDate.getDay();
  const isWeekend = dow === 0 || dow === 6;

  return (
    // Keyed by date and with keyed loading/events branches so a reload never
    // reconciles stale cards against the new list (left orphan cards behind).
    <div key={key} className={`cal-day-view${isWeekend ? ' is-weekend' : ''}`}>
      {isLoading ? (
        <React.Fragment key="loading">
          <div className="cal-skeleton cal-skeleton-card" style={{ marginBottom: 6 }} />
          <div className="cal-skeleton cal-skeleton-card" style={{ marginBottom: 6 }} />
          <div className="cal-skeleton cal-skeleton-card" />
        </React.Fragment>
      ) : dayEvents.length === 0 ? (
        <div className="cal-day-empty">No events for this day.</div>
      ) : (
        <React.Fragment key="events">
          {dayEvents.map(ev => (
            <CalendarEventCard
              key={getCalendarEventKey(ev)}
              event={ev}
              onOpenInfoWindow={onOpenInfoWindow}
              actionMenuConfig={actionMenuConfig}
              onDuplicateItem={onDuplicateItem}
            />
          ))}
        </React.Fragment>
      )}
    </div>
  );
};

export default CalendarDayView;
